import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Card, CheckIcon } from '@/components/ui';
import { useReferral } from '@/lib/queries';
import { InviteeList } from './InviteeList';

/**
 * Prize-draw section of the profile (screen 06, P-08): one chance per invitee who paid
 * (decision 0006), and the invitees with their state.
 */
export function ReferralCard() {
  const { t } = useTranslation();
  const { data } = useReferral();
  const location = useLocation();
  const navigate = useNavigate();
  // Set by InviteForm when every address was sent (decision 0007). Read once, then dropped
  // from the history entry so a reload does not confirm the same invitations again.
  const [justSent] = useState(
    () => (location.state as { invitesSent?: number } | null)?.invitesSent ?? 0,
  );
  useEffect(() => {
    if (justSent) navigate(location.pathname, { replace: true, state: null });
  }, [justSent, navigate, location.pathname]);

  if (!data) return null;
  return (
    <Card aria-labelledby="referral-title">
      <div className="flex items-baseline justify-between">
        <h2 id="referral-title" className="m-0 font-display text-section font-bold">
          {t('referral.title')}
        </h2>
        <span className="font-display text-section font-bold text-accent">
          {t('referral.chances', { count: data.chances })}
        </span>
      </div>
      {justSent > 0 && (
        <p
          role="status"
          className="m-0 flex items-center gap-1.5 text-sm font-semibold text-success"
        >
          <CheckIcon size={16} />
          {t('referral.sent', { count: justSent })}
        </p>
      )}
      {data.invites.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">{t('referral.empty')}</p>
      ) : (
        <InviteeList invites={data.invites} variant="plain" />
      )}
      {data.viaLinkCount > 0 && (
        <p className="m-0 text-sm font-semibold text-success">
          {t('referral.viaLink', { count: data.viaLinkCount })}
        </p>
      )}
      <Link to="/me/invite" className="self-start text-sm font-semibold no-underline">
        {t('referral.inviteMore')}
      </Link>
    </Card>
  );
}
